import { FC, useEffect, useState } from "react";
import { LoggedInUser } from "@mksap/types/loggedInUser";
import {
	getUserLocalStorage,
	UserLocalStorageData,
} from "@mksap/utils/userLocalStorage";
import { getSyncApi } from "@mksap/serverApis";
import { makeAjaxPromise } from "@mksap/utils/makeAjaxPromise";
import { SyncResult } from "@mksap/types/sync";
import LoginBackend from "@mksap/backends/LoginBackend";

type SyncState = NonNullable<UserLocalStorageData["sync"]>;

interface Props {
	loggedInUser: LoggedInUser;
}

type SyncStatus = "idle" | "syncing" | "success" | "error";

export const NativeAppInitializationError: FC<Props> = ({ loggedInUser }) => {
	const [syncState, setSyncState] = useState<SyncState | null>(null);
	const [mode, setMode] = useState<
		"loading-state" | "has-pending-actions" | "no-pending-actions"
	>("loading-state");
	const [resettingApp, setResettingApp] = useState(false);

	useEffect(() => {
		getUserLocalStorage(loggedInUser.acpNumber)
			.then((storage) => storage.getData())
			.then((data) => {
				setSyncState(data?.sync ?? null);
				if (data?.sync?.pendingSyncActions.length) {
					setMode("has-pending-actions");
				} else {
					setMode("no-pending-actions");
				}
			})
			.catch(() => {
				// TODO: Maybe post the full localStorage data somewhere?
				console.warn("Error loading user local storage data");
				setMode((prevMode) =>
					prevMode === "loading-state" ? "no-pending-actions" : prevMode,
				);
			});
	}, [loggedInUser.acpNumber]);

	const handleResetApp = async () => {
		setResettingApp(true);
		try {
			const userLocalStorage = await getUserLocalStorage(
				loggedInUser.acpNumber,
			);
			await userLocalStorage.resetData();
		} catch (err) {
			console.error("Error resetting user local storage:", err);
		} finally {
			LoginBackend.logOut();
		}
	};

	return (
		<div className="alert alert-danger">
			{mode === "no-pending-actions" && (
				<NoPendingActionsMessage
					handleResetApp={handleResetApp}
					resettingApp={resettingApp}
				/>
			)}
			{mode === "has-pending-actions" && (
				<PendingActionsMessage
					syncState={syncState!}
					setSyncState={setSyncState}
					handleResetApp={handleResetApp}
					resettingApp={resettingApp}
				/>
			)}
		</div>
	);
};

const NoPendingActionsMessage: FC<{
	handleResetApp: () => void;
	resettingApp: boolean;
}> = ({ handleResetApp, resettingApp }) => {
	return (
		<div>
			<h4>Something went wrong</h4>
			<p>
				MKSAP couldn&apos;t start. Reload the app to try again. You&apos;ll need
				to log in again afterward.
			</p>
			<button
				className="btn btn-primary"
				onClick={handleResetApp}
				disabled={resettingApp}
			>
				{resettingApp ? "Reloading..." : "Reload"}
			</button>
		</div>
	);
};

const PendingActionsMessage: FC<{
	syncState: SyncState;
	setSyncState: React.Dispatch<React.SetStateAction<SyncState | null>>;
	handleResetApp: () => void;
	resettingApp: boolean;
}> = ({ syncState, setSyncState, handleResetApp, resettingApp }) => {
	const [syncStatus, setSyncStatus] = useState<SyncStatus>("idle");

	const handleSync = async () => {
		if (!syncState) return;
		setSyncStatus("syncing");
		try {
			const syncTime = Date.now();
			const pendingSyncActions = syncState.pendingSyncActions.map((action) => ({
				...action,
				age: (syncTime - action.createdAt) / 1000,
			}));
			const syncResult = await makeAjaxPromise<SyncResult>({
				url: getSyncApi(),
				type: "POST",
				contentType: "application/json",
				data: JSON.stringify({
					last_sync_id: syncState.lastSyncId,
					sync_actions: pendingSyncActions,
				}),
			});

			const syncedActionIds = new Set(syncResult.loggedSyncActions ?? []);
			setSyncState((prevState) => {
				if (!prevState) {
					return prevState;
				}

				return {
					...prevState,
					lastSyncId: syncResult.syncId,
					lastSyncTime: syncResult.syncTime,
					pendingSyncActions: prevState.pendingSyncActions.filter(
						(action) => !syncedActionIds.has(action.id),
					),
				};
			});
			setSyncStatus("success");
		} catch (err) {
			console.error("Error syncing:", err);
			setSyncStatus("error");
		}
	};
	const pendingCount = syncState?.pendingSyncActions.length ?? 0;
	const unsyncedResponseCount =
		syncState?.pendingSyncActions.filter(
			(action) => action.type === "ADD_RESPONSE",
		).length ?? 0;

	const didSyncSuccessfully = syncStatus === "success" && pendingCount === 0;

	return (
		<>
			{didSyncSuccessfully ? (
				<div>
					<h4>Your work is saved</h4>
					<p>
						Your responses have been backed up. Reload the app to finish fixing
						the issue. You&apos;ll need to log in again afterward.
					</p>
					<button
						className="btn btn-primary"
						onClick={handleResetApp}
						disabled={resettingApp}
					>
						{resettingApp ? "Reloading..." : "Reload"}
					</button>
				</div>
			) : (
				<div>
					<h4>Something went wrong</h4>
					<p>
						MKSAP couldn&apos;t start. Reload the app to fix this, but sync your
						work first so nothing is lost.
					</p>
					<p>
						You have {unsyncedResponseCount} unsaved question{" "}
						{unsyncedResponseCount === 1 ? "response" : "responses"}.
					</p>
					<button
						className="btn btn-primary"
						onClick={handleSync}
						disabled={syncStatus === "syncing"}
					>
						{syncStatus === "syncing"
							? "Syncing..."
							: "Back up & continue \u2192"}
					</button>
					{syncStatus === "error" && (
						<p>There was an error syncing. Please try again.</p>
					)}
				</div>
			)}
		</>
	);
};
